import React, { useState } from 'react';
import {
  Box,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Alert,
  Chip,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Delete } from '@mui/icons-material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ConsumableBuyAndSellRateType,
  deleteBuyRate,
  deleteSellRate,
  RateType,
} from 'api/consumablesApi';
import ConfirmDeleteDialog from 'components/ConfirmDeleteDialog/ConfirmDeleteDialog';
import BusinessIcon from '@mui/icons-material/Business';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import { blue, blueGrey, grey } from '@mui/material/colors';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import { currencyType } from 'api/organizationApi';

interface DetailsViewTableRowProps {
  row: { key: string; rates: ConsumableBuyAndSellRateType[] };
  type?: RateType;
  handleConsumableClose: () => void;
}

function DetailsViewTableRow({
  row,
  type,
  handleConsumableClose,
}: DetailsViewTableRowProps) {
  const [open, setOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] =
    useState<ConsumableBuyAndSellRateType | null>(null);
  const queryClient = useQueryClient();

  const deleteConsumableSellRateMutation = useMutation(deleteSellRate, {
    onSuccess: (data) => {
      if (data.consumableItemDeleted) {
        handleConsumableClose();
        enqueueSnackbar('Consumable items deleted successfully', {
          variant: 'success',
        });
      }
      queryClient.invalidateQueries(['consumables-items']);
    },
    onError: (error) => {
      console.error('Error deleting sell rate:', error);
    },
  });

  const deleteConsumableBuyRateMutation = useMutation(deleteBuyRate, {
    onSuccess: (data) => {
      if (data.consumableItemDeleted) {
        handleConsumableClose();

        enqueueSnackbar('Consumable items deleted successfully', {
          variant: 'success',
        });
      }
      queryClient.invalidateQueries(['consumables-items']);
    },
    onError: (error) => {
      console.error('Error deleting buy rate:', error);
    },
  });

  const handleDelete = async () => {
    if (!selectedItem) return;
    if (type === RateType.SellRate) {
      deleteConsumableSellRateMutation.mutate(selectedItem.id);
    } else {
      deleteConsumableBuyRateMutation.mutate(selectedItem.id);
    }
    setDeleteDialogOpen(false);
    setSelectedItem(null);
  };

  const openDeleteDialog = (item: ConsumableBuyAndSellRateType) => {
    setSelectedItem(item);
    setDeleteDialogOpen(true);
  };

  return (
    <>
      <TableRow>
        <TableCell colSpan={3}>
          <Box display="flex" alignItems="center">
            <IconButton
              aria-label="expand row"
              size="medium"
              onClick={() => setOpen(!open)}>
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>

            {row.key.split('/').map((part, index) => (
              <Chip
                key={`${row.key}-${index}`}
                label={part}
                variant="outlined"
                sx={{
                  margin: '2px',
                  padding: '4px 4px',
                  color: '#000',
                  // backgroundColor:
                  //   type === RateType.BuyRate ? blueGrey[200] : blue[200],
                  paddingInline: '0.5rem',
                  paddingY: '1rem',
                  borderColor:
                    type === RateType.BuyRate ? blueGrey[200] : blue[200],
                }}
                color="primary"
                size="small"
                icon={index === 0 ? <BusinessIcon /> : <AccountBoxIcon />}
              />
            ))}
          </Box>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={3}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Table size="small" aria-label="history">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ border: 'none' }}>
                      Effective From
                    </TableCell>
                    <TableCell sx={{ border: 'none' }}>
                      {type === RateType.SellRate ? 'Sell Price' : 'Buy Price'}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ border: 'none' }}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {row.rates.map((item) => (
                    <TableRow key={item?.consumableItemId}>
                      <TableCell sx={{ border: 'none' }}>
                        {format(new Date(item.effectiveFrom), 'dd MMM yyyy')}
                      </TableCell>
                      <TableCell sx={{ border: 'none' }}>
                        {currencyType}
                        {Number(
                          type === RateType.SellRate
                            ? item.sellRate
                            : item.buyRate
                        ).toFixed(4) || '-'}
                      </TableCell>
                      <TableCell align="right" sx={{ border: 'none' }}>
                        {/* <Button
                          size="small"
                          variant="outlined"
                          sx={{ marginRight: '0.5rem' }}
                          startIcon={<EditIcon />}
                          onClick={() => {
                            handleOpenEditRatesDialog(item);
                          }}>
                          Edit
                        </Button> */}
                        <IconButton onClick={() => openDeleteDialog(item)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      {deleteDialogOpen && selectedItem && (
        <ConfirmDeleteDialog
          open={deleteDialogOpen}
          title="Delete Confirmation"
          content={
            <>
              Are you sure you want to delete the item with the effective date{' '}
              <b>{new Date(selectedItem.effectiveFrom).toDateString()}</b>?
              <Alert severity="warning" style={{ marginTop: '1rem' }}>
                This action is irreversible and will permanently delete the
                data.
              </Alert>
            </>
          }
          deleteFunc={handleDelete} // Pass the delete function here
          handleReject={() => {
            setDeleteDialogOpen(false);
            setSelectedItem(null);
          }}
          handleClose={() => {
            setDeleteDialogOpen(false);
            setSelectedItem(null);
          }}
        />
      )}
    </>
  );
}

interface ConsumableSellAndBuyDetailsViewTableProps {
  consumable: { key: string; rates: ConsumableBuyAndSellRateType[] }[];
  type: 'sell' | 'buy';
  handleConsumableClose: () => void;
}
export default function ConsumableSellAndBuyDetailsViewTable({
  consumable,
  type,
  handleConsumableClose,
}: ConsumableSellAndBuyDetailsViewTableProps) {
  return (
    <Box>
      <Typography variant="h6" sx={{ fontSize: '1.2rem', pl: 1 }} gutterBottom>
        {type === 'sell' ? 'Sell Rates ' : 'Buy Rates'}
      </Typography>
      <TableContainer
        component={Paper}
        sx={{ border: '1px solid', borderColor: grey[300], borderRadius: 2 }}
        elevation={0}>
        <Table>
          <TableHead sx={{ backgroundColor: grey[50] }}>
            <TableRow>
              <TableCell>Branch / Client</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {consumable && consumable.length > 0 ? (
              consumable.map((row, index) => (
                <DetailsViewTableRow
                  key={index}
                  row={row}
                  type={type === 'sell' ? RateType.SellRate : RateType.BuyRate}
                  handleConsumableClose={handleConsumableClose}
                />
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  No data available.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
